import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LayoutDashboard, Plus, Search, Check, Hash, LineChart, BarChart3, PieChart, Table2, Gauge, Filter, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import ReportWidget from "@/components/reports/ReportWidget";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TYPE_META = {
  stat: { icon: Hash, label: "Stat tile", color: "bg-slate-100 text-slate-700" },
  line: { icon: LineChart, label: "Line chart", color: "bg-cyan-50 text-cyan-700" },
  bar: { icon: BarChart3, label: "Bar chart", color: "bg-sky-50 text-sky-700" },
  pie: { icon: PieChart, label: "Pie chart", color: "bg-amber-50 text-amber-700" },
  table: { icon: Table2, label: "Table", color: "bg-slate-100 text-slate-700" },
  gauge: { icon: Gauge, label: "Goal dial", color: "bg-emerald-50 text-emerald-700" },
  funnel: { icon: Filter, label: "Pipeline funnel", color: "bg-indigo-50 text-indigo-700" },
  tasks: { icon: CheckSquare, label: "Task list", color: "bg-rose-50 text-rose-700" },
};

function SortableWidget({ widget, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const span = widget.size === "sm" ? "col-span-1" : widget.size === "full" ? "col-span-2 lg:col-span-4" : "col-span-2";
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`${span} ${isDragging ? "opacity-60 z-20 relative" : ""}`} data-testid={`widget-slot-${widget.id}`}>
      <ReportWidget widget={widget} onRemove={onRemove} plain dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function WidgetMenu({ onClose, selected, onToggle, saving }) {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["widget-library"], queryFn: async () => (await api.get("/reports/widgets/library")).data });
  const widgets = (data?.widgets || []).filter((w) =>
    `${w.title} ${w.subtitle || ""} ${w.type}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[88vh] flex flex-col" data-testid="add-widget-menu">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus size={16} className="text-cyan-600" />Add Widgets</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search widgets… revenue, funnel, supplier"
            className="rounded-sm pl-8" data-testid="widget-search-input" />
        </div>
        <div className="flex-1 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2 pr-1" data-testid="widget-menu-list">
          {widgets.map((w) => {
            const meta = TYPE_META[w.type] || TYPE_META.stat;
            const Icon = meta.icon;
            const on = selected.includes(w.id);
            return (
              <button key={w.id} disabled={saving} onClick={() => onToggle(w.id)}
                className={`flex items-start gap-3 text-left border rounded-sm px-3 py-3 transition-colors ${on ? "border-cyan-500 bg-cyan-50/50" : "border-slate-200 hover:border-cyan-300"}`}
                data-testid={`library-toggle-${w.id}`}>
                <span className={`shrink-0 w-12 h-12 rounded-sm flex items-center justify-center ${meta.color}`} data-testid={`widget-thumb-${w.id}`}>
                  <Icon size={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800 leading-tight">{w.title}</span>
                    <span className={`text-[8.5px] font-bold tracking-wider uppercase px-1.5 py-px rounded-sm ${w.visibility === "management" ? "bg-slate-900 text-cyan-300" : "bg-slate-100 text-slate-500"}`}>
                      {w.visibility === "management" ? "MGMT" : "PUBLIC"}
                    </span>
                  </span>
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-slate-400 mt-0.5">{meta.label}</span>
                  {w.subtitle && <span className="block text-[11px] text-slate-400 mt-0.5 line-clamp-2">{w.subtitle}</span>}
                </span>
                <span className={`shrink-0 mt-0.5 text-[11px] font-bold flex items-center gap-1 ${on ? "text-cyan-700" : "text-slate-400"}`}>
                  {on ? <><Check size={13} />Added</> : <><Plus size={13} />Add</>}
                </span>
              </button>
            );
          })}
          {widgets.length === 0 && (
            <p className="col-span-full text-sm text-slate-400 py-6 text-center" data-testid="widget-menu-empty">
              No widgets match "{q}". New widgets are built on the Reports page.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyDashboard() {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);

  const { data } = useQuery({ queryKey: ["my-dashboard"], queryFn: async () => (await api.get("/my-dashboard")).data });
  const widgets = data?.widgets || [];
  const ids = widgets.map((w) => w.id);

  const saveMut = useMutation({
    mutationFn: async (widget_ids) => (await api.put("/my-dashboard", { widget_ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-dashboard"] }),
    onError: () => toast.error("Could not save your dashboard"),
  });

  const toggle = (wid) => saveMut.mutate(ids.includes(wid) ? ids.filter((i) => i !== wid) : [...ids, wid]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    saveMut.mutate(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Your dashboard, your way — start blank and add the widgets you want to see every day."
        breadcrumb={["Home", "Dashboard"]}
        actions={
          <Button onClick={() => setMenuOpen(true)} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-widgets-btn">
            <Plus size={15} className="mr-1" />Add widget
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        {widgets.length === 0 ? (
          <Card className="rounded-sm border-dashed border-2 border-slate-200 p-12 text-center" data-testid="dashboard-empty">
            <LayoutDashboard size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-600">Your dashboard is blank</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Pick the reports, dials and funnels you want from the widget library.</p>
            <Button onClick={() => setMenuOpen(true)} variant="outline" className="rounded-sm" data-testid="add-first-widget-btn">
              <Plus size={15} className="mr-1" />Add your first widget
            </Button>
          </Card>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-grid">
                {widgets.map((w) => <SortableWidget key={w.id} widget={w} onRemove={toggle} />)}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {widgets.length > 1 && (
          <p className="text-[11px] text-slate-400 mt-3">Drag the ⠿ handle on any widget to rearrange your dashboard.</p>
        )}
      </div>
      {menuOpen && <WidgetMenu onClose={() => setMenuOpen(false)} selected={ids} onToggle={toggle} saving={saveMut.isPending} />}
    </div>
  );
}
